import { useState, useEffect, useCallback } from 'react';
import { getTodayMeals, createMeal, updateMeal, deleteMeal } from '@/lib/firebase/queries/meals';
import type { Meal, CreateMealParams, UpdateMealParams } from '@/lib/firebase/queries/meals';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';

export function useMeals() {
  const [meals, setMeals] = useState<Meal[]>([]);
  const [loading, setLoading] = useState(true);
  const { currentUser } = useAuth();
  const { t } = useLanguage();

  const fetchMeals = useCallback(async () => {
    if (!currentUser?.uid) {
      setMeals([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const todayMeals = await getTodayMeals(currentUser.uid);
      setMeals(todayMeals);
    } catch (error) {
      console.error('Error fetching meals:', error);
      toast.error(t('meals.error.fetch') || 'Failed to fetch meals');
    } finally {
      setLoading(false);
    }
  }, [currentUser?.uid, t]);

  useEffect(() => {
    fetchMeals();
  }, [fetchMeals]);

  const addMeal = async (params: Omit<CreateMealParams, 'userId'>) => {
    if (!currentUser?.uid) return null;

    try {
      const mealId = await createMeal({
        ...params,
        userId: currentUser.uid
      });
      await fetchMeals();
      toast.success(t('meals.success.created') || 'Meal logged successfully');
      return mealId;
    } catch (error) {
      console.error('Error creating meal:', error);
      toast.error(t('meals.error.create') || 'Failed to log meal');
      return null;
    }
  };

  const editMeal = async (mealId: string, params: UpdateMealParams) => {
    try {
      await updateMeal(mealId, params);
      await fetchMeals();
      toast.success(t('meals.success.updated') || 'Meal updated successfully');
      return true;
    } catch (error) {
      console.error('Error updating meal:', error);
      toast.error(t('meals.error.update') || 'Failed to update meal');
      return false;
    }
  };

  const removeMeal = async (mealId: string) => {
    try {
      await deleteMeal(mealId);
      await fetchMeals();
      toast.success(t('meals.success.deleted') || 'Meal deleted successfully');
      return true;
    } catch (error) {
      console.error('Error deleting meal:', error);
      toast.error(t('meals.error.delete') || 'Failed to delete meal');
      return false;
    }
  };

  return {
    meals,
    loading,
    addMeal,
    editMeal,
    removeMeal,
    refreshMeals: fetchMeals
  };
}


